import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { FileText, Download, Trash2, Search, Filter, Grid, List as ListIcon, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const DocumentList = () => {
    const { token } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('All');
    const [filterPerson, setFilterPerson] = useState('All'); // New Filter
    const [viewMode, setViewMode] = useState('list');
    const [members, setMembers] = useState([]);

    const fetchDocuments = async () => {
        try {
            const response = await axios.get('http://localhost:4000/api/documents', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDocuments(response.data.data);
        } catch (error) {
            console.error('Failed to fetch documents', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch members for filter
    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const res = await axios.get('http://localhost:4000/api/auth/members', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setMembers(res.data.data.members || []);
            } catch (error) {
               // Silent fail if no members
            }
        };
        fetchMembers();
        fetchDocuments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const downloadDocument = async (id, title) => {
        try {
            const response = await axios.get(`http://localhost:4000/api/documents/${id}/download`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${title}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Download failed', error);
            alert('Download failed. Note: You must be the owner or admin.');
        }
    };

    const deleteDocument = async (id) => {
        if (!window.confirm('Are you sure you want to delete this document? This cannot be undone.')) return;

        try {
            await axios.delete(`http://localhost:4000/api/documents/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            // Optimistic update
            setDocuments(prev => prev.filter(doc => doc._id !== id));
        } catch (error) {
            console.error('Delete failed', error);
            alert('Failed to delete document. You might not have permission.');
        }
    };

    const filteredDocs = documents.filter(doc => {
        const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                              (doc.related_person_name && doc.related_person_name.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesCategory = filterCategory === 'All' || doc.category === filterCategory;
        const matchesPerson = filterPerson === 'All' || doc.related_person_name === filterPerson;
        return matchesSearch && matchesCategory && matchesPerson;
    });

    return (
        <Layout>
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header & Filters */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Documents</h1>
                        <p className="text-sm text-gray-500">Manage family records securely.</p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto flex-wrap">
                        <div className="relative flex-1 sm:w-48">
                            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search..."
                                className="pl-10 w-full rounded-xl border-gray-200 bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600 p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Person Filter */}
                        <div className="relative">
                            <select
                                className="pl-4 pr-10 py-3 rounded-xl border-gray-200 bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                                value={filterPerson}
                                onChange={(e) => setFilterPerson(e.target.value)}
                            >
                                <option value="All">All Family</option>
                                {members.map(m => (
                                    <option key={m._id} value={m.email.split('@')[0]}>{m.email.split('@')[0]}</option>
                                ))}
                                {/* Also include unique names from docs in case they are not in members list (legacy) */}
                                {[...new Set(documents.map(d => d.related_person_name).filter(Boolean))]
                                    .filter(n => !members.find(m => m.email.split('@')[0] === n))
                                    .map(name => (
                                        <option key={name} value={name}>{name}</option>
                                    ))
                                }
                            </select>
                            <User className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>

                        <div className="relative">
                            <select
                                className="pl-4 pr-10 py-3 rounded-xl border-gray-200 bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                            >
                                <option value="All">Categories</option>
                                <option value="Personal & Identity">Personal</option>
                                <option value="Financial Records">Financial</option>
                                <option value="Insurance">Insurance</option>
                                <option value="Medical">Medical</option>
                                <option value="Property">Property</option>
                                <option value="Legal">Legal</option>
                            </select>
                            <Filter className="absolute right-3 top-3 h-5 w-5 text-gray-400 pointer-events-none" />
                        </div>

                        <div className="bg-gray-100 dark:bg-gray-700 p-1 rounded-xl flex items-center">
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'text-gray-500'}`}
                            >
                                <ListIcon className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'text-gray-500'}`}
                            >
                                <Grid className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="min-h-[400px]">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                        </div>
                    ) : filteredDocs.length > 0 ? (
                        <AnimatePresence>
                            {viewMode === 'list' ? (
                                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700">
                                    <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {filteredDocs.map((doc, i) => (
                                            <motion.li
                                                key={doc._id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: i * 0.05 }}
                                                className="group hover:bg-blue-50/50 dark:hover:bg-gray-700/30 transition-colors duration-200"
                                            >
                                                <div className="px-6 py-5 flex items-center justify-between">
                                                    <div className="flex items-center min-w-0 flex-1 gap-4">
                                                        <div className="flex-shrink-0 bg-blue-100/50 dark:bg-blue-900/20 p-3 rounded-xl">
                                                            <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="truncate text-sm font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors">
                                                                {doc.title}
                                                            </p>
                                                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                                {doc.related_person_name && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                                                                        <User className="w-3 h-3 mr-1" />
                                                                        {doc.related_person_name}
                                                                    </span>
                                                                )}
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                                                    {doc.category}
                                                                    {doc.sub_category ? ` / ${doc.sub_category}` : ''}
                                                                </span>
                                                                <span className="text-xs text-gray-400">
                                                                    • {(doc.size / 1024 / 1024).toFixed(2)} MB
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                        <button
                                                            onClick={() => downloadDocument(doc._id, doc.title)}
                                                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-100 rounded-full transition-colors"
                                                            title="Download"
                                                        >
                                                            <Download className="h-5 w-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => deleteDocument(doc._id)}
                                                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-100 rounded-full transition-colors"
                                                            title="Delete"
                                                        >
                                                            <Trash2 className="h-5 w-5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </motion.li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {filteredDocs.map((doc, i) => (
                                        <motion.div
                                            key={doc._id}
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: i * 0.05 }}
                                            className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-100 dark:border-gray-700 relative group"
                                        >
                                            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                                                <button onClick={() => downloadDocument(doc._id, doc.title)} className="p-1.5 bg-white dark:bg-gray-700 rounded-lg shadow-sm hover:text-blue-500">
                                                    <Download className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => deleteDocument(doc._id)} className="p-1.5 bg-white dark:bg-gray-700 rounded-lg shadow-sm hover:text-red-500">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>

                                            <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center mb-4">
                                                <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <h3 className="font-semibold text-gray-900 dark:text-white truncate" title={doc.title}>
                                                {doc.title}
                                            </h3>
                                            
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                 {doc.related_person_name && (
                                                    <p className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                                                        {doc.related_person_name}
                                                    </p>
                                                )}
                                                <p className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{doc.category}</p>
                                            </div>

                                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between text-xs text-gray-400">
                                                <span>{(doc.size / 1024 / 1024).toFixed(1)} MB</span>
                                                <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </AnimatePresence>
                    ) : (
                        <div className="flex flex-col items-center justify-center p-12 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-full mb-4">
                                <Search className="h-8 w-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">No documents found</h3>
                            <p className="mt-1 text-gray-500">Try adjusting your search or filters.</p>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default DocumentList;
